import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { Badge } from "@/components/StatusBadge";
import { FieldRow } from "@/components/record/FieldRow";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ApiError, apiFetch } from "@/lib/api";
import type { BackupRunResource, BackupStatusResource, Page } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { formatBytes, formatDateCell, formatOutcome } from "@/lib/format";

import { ColumnPickerDialog } from "./ColumnPickerDialog";

/**
 * Settings → Backup (Story 4.5, R-B8, R-B6's visible half): the third real
 * child of `/settings`, alongside Users and Alerts. Three top status
 * figures — reusing `RecordPage`'s own `FieldRow` vocabulary directly
 * (spec Code Map: "even without a literal single-entity record page" —
 * there is no single `BackupStatusResource` "record" with an id to hand
 * `RecordPage` itself, so this screen composes `FieldRow` the same way
 * `RecordPage` does internally, rather than wrapping a shape that does not
 * exist) — and a `DataTable` of recent `BackupRun` history. `GET
 * /backup-status`/`GET /backup-runs` are both `role=Role.READ_ONLY`
 * (judgement call 5): every figure and the whole table render for a
 * Read-only account. The "Download an archive" affordance is a plain
 * `<a href>` navigation, never `apiFetch`/`fetch` (Boundaries: a large,
 * streamed file download is never a good fit for an in-memory `fetch`
 * response, and the architecture guard `csrf-header-guard.test.ts` forbids
 * bare `fetch` outside `lib/api.ts` regardless) — withheld entirely
 * (`useIsOwner`, never merely disabled) for anyone but a confirmed Owner,
 * mirroring `AlertsList.tsx`'s own identical Owner-only-affordance pattern;
 * the server's own `role=Role.OWNER` on the route is the real enforcement.
 */
export function BackupStatus() {
  const isOwner = useIsOwner();
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

  useEffect(() => {
    document.title = "Backup — PropDesk";
  }, []);

  const statusQuery = useQuery({
    queryKey: ["backup-status"],
    queryFn: () => apiFetch<BackupStatusResource>("/backup-status"),
  });

  const listQueryKey = ["backup-runs", "list"] as const;
  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<BackupRunResource>>("/backup-runs"),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<BackupRunResource>[] = [
    { key: "run_at", tabularFigures: true, render: (row) => formatDateCell(row.run_at) },
    { key: "outcome", render: (row) => formatOutcome(row.outcome) },
    { key: "size_bytes", align: "right", tabularFigures: true, render: (row) => formatBytes(row.size_bytes) },
  ];

  const listErrorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;
  const statusErrorMessage = statusQuery.error instanceof ApiError ? statusQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Backup</h1>
      <p className="mb-6 mt-2 text-body text-ink-secondary">
        The real state of PropDesk&apos;s own nightly backup, its monthly restore test, and the
        off-box heartbeat that watches both.
      </p>

      {statusQuery.isError ? (
        // A genuinely failed fetch (a lost session, a network error, ...)
        // is never rendered as if it were the honest "nothing has
        // succeeded yet" empty state below — that would say something
        // false about the real system. Mirrors `DataTable`'s own
        // `isError` treatment for the history table just below.
        <div className="mb-8 rounded-lg border border-border-hairline bg-surface p-6 text-body text-ink">
          <p>Couldn&apos;t load the backup status.</p>
          {statusErrorMessage ? <p className="mt-1 text-ink-secondary">{statusErrorMessage}</p> : null}
        </div>
      ) : (
        <div className="mb-8 divide-y divide-border-hairline rounded-lg border border-border-hairline bg-surface px-4">
          <FieldRow label="Last successful backup">
            {statusQuery.isPending ? (
              "…"
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {statusQuery.data?.last_successful_backup
                  ? formatDateCell(statusQuery.data.last_successful_backup.run_at)
                  : "No backup has completed successfully yet."}
                {/* VC-4.5-03: a missing BackupRun is never rendered as merely
                 * "not overdue" — the server's own `is_overdue` (always
                 * `True` when there is no successful backup at all) is what
                 * this reads, never a client-side guess. */}
                {statusQuery.data?.is_overdue ? <Badge label="Overdue" fill="overdue" /> : null}
              </span>
            )}
          </FieldRow>
          <FieldRow label="Last successful restore test">
            {statusQuery.isPending
              ? "…"
              : statusQuery.data?.last_successful_restore_test
                ? formatDateCell(statusQuery.data.last_successful_restore_test.run_at)
                : "No restore test has completed successfully yet."}
          </FieldRow>
          <FieldRow label="Last heartbeat ping">
            {statusQuery.isPending
              ? "…"
              : statusQuery.data?.last_backup_heartbeat_at
                ? formatDateCell(statusQuery.data.last_backup_heartbeat_at)
                : "No heartbeat ping has succeeded yet."}
          </FieldRow>
          {/* Story 7.1: the real, real-time total every uploaded document
           * occupies on disk — the same figure the `storage-threshold`
           * alert rule evaluates against server-side. This is the whole
           * of this story's own frontend surface (Boundaries: no upload
           * UI, no documents list — Story 7.2's own job; the Phase 1b
           * Data health dashboard panel is explicitly out of scope too). */}
          <FieldRow label="Storage used">
            {statusQuery.isPending ? "…" : formatBytes(statusQuery.data?.storage_used_bytes ?? null)}
          </FieldRow>
          {/* Story 8.6 (VC-8.6-19, Tier A fix round, 2026-08-21): the
           * monthly portfolio CSV export's own watermark, mirroring "Last
           * successful backup"'s exact same shape above — an honest empty
           * state when no export has ever succeeded, and the server-decided
           * `is_csv_export_overdue` (never a client-side guess) driving the
           * same Overdue badge. */}
          <FieldRow label="Last portfolio CSV export">
            {statusQuery.isPending ? (
              "…"
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {statusQuery.data?.last_csv_export_at
                  ? formatDateCell(statusQuery.data.last_csv_export_at)
                  : "No portfolio export has completed successfully yet."}
                {statusQuery.data?.is_csv_export_overdue ? <Badge label="Overdue" fill="overdue" /> : null}
              </span>
            )}
          </FieldRow>
        </div>
      )}

      {isOwner ? (
        <div className="mb-6 flex flex-wrap gap-3">
          <a
            href="/backup-runs/download"
            className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
          >
            Download an archive
          </a>
          {/* Story 8.6 (AC1, VC-8.6-19): a plain `<a href>` navigation to
           * `GET /exports/portfolio.zip`, exactly mirroring "Download an
           * archive" just above — a real, immediate, streamed browser
           * download, never `apiFetch`/`fetch` (same Boundaries reasoning:
           * a large streamed file is never a good `fetch` response, and
           * `csrf-header-guard.test.ts` forbids bare `fetch` outside
           * `lib/api.ts` regardless). The browser's own native
           * download-progress UI satisfies "runs as a background job with
           * visible progress" (spec judgement call 2) — no custom progress
           * UI is built here. */}
          <a
            href="/exports/portfolio.zip"
            className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
          >
            Export portfolio
          </a>
          {/* Story 15.4 (R-I2's backlog item 4): a tailored alternative to
           * "Export portfolio" above, never a replacement for it — opens
           * `ColumnPickerDialog`, which itself carries the real trigger for
           * the tailored download (its own `<a href>` navigation, built
           * from the picker's own selection). */}
          <Button type="button" variant="secondary" onClick={() => setIsColumnPickerOpen(true)}>
            Choose columns…
          </Button>
        </div>
      ) : null}

      <ColumnPickerDialog open={isColumnPickerOpen} onOpenChange={setIsColumnPickerOpen} />

      <h2 className="mb-3 text-title font-semibold tracking-title text-ink">Backup history</h2>
      <DataTable
        caption="Backup history"
        itemNamePlural="backup runs"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(listErrorMessage ? { errorMessage: listErrorMessage } : {})}
        getRowHref={() => "/settings/backup"}
        emptyState={{
          heading: "No backup runs recorded yet.",
          body: "The first nightly backup run will appear here once it completes.",
        }}
      />
    </div>
  );
}
